import { useEffect, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { callBackend, useMutation, usePaginatedQuery, useQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { getPhoneTypeLabel } from '@/utils/phoneUtils';
import type { ContactPhoneType } from '@/types';
import { useCrm } from './CrmContext';
import { ExtensionPicker, Field, TagInput, Toggle } from './fields';
import { buttonClass, errorMessage, inputClass } from './format';
import { LIFECYCLE_LABELS, type CrmCompany, type CrmContact, type Lifecycle } from './types';

const PHONE_TYPES: ContactPhoneType[] = ['mobile', 'business', 'home', 'mobile2', 'business2', 'home2', 'business_fax', 'home_fax', 'other'];

export function ContactEditor({
  contact,
  initialPhone,
  suggestion,
  onClose,
  onSaved,
}: {
  contact?: CrmContact | null;
  initialPhone?: string;
  /** A suggested name and company for an unnamed caller (Unknown caller look-up). */
  suggestion?: { name: string; company?: string } | null;
  onClose: () => void;
  onSaved?: (contactId: string) => void;
}) {
  const { data } = useCrm();
  const saveContact = useMutation(backendApi.crm.saveContact);
  const tags = useQuery<Array<{ tag: string }>>(backendApi.crm.listTags, {});
  const customFields = data?.settings.customFields ?? [];
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: contact?.kind === 'contact' ? contact.name : suggestion?.name ?? '',
    jobTitle: contact?.jobTitle ?? '',
    company: contact?.company ?? suggestion?.company ?? '',
    email: contact?.email ?? '',
    remark: contact?.remark ?? '',
    lifecycle: (contact?.lifecycle ?? '') as Lifecycle | '',
    ownerExtension: contact?.ownerExtension ?? '',
    ownerName: contact?.ownerName ?? '',
    vip: contact?.vip ?? false,
    tags: contact?.tags ?? [],
    customFields: contact?.customFields ?? {},
  });
  const [phones, setPhones] = useState<Array<{ type: string; number: string }>>(
    contact?.phones.length
      ? contact.phones.map((phone) => ({ type: phone.type === 'other' && contact.kind === 'caller' ? 'mobile' : phone.type, number: phone.number }))
      : [{ type: 'mobile', number: initialPhone ?? '' }]
  );
  const [companySearch, setCompanySearch] = useState(form.company);
  useEffect(() => {
    const timer = setTimeout(() => setCompanySearch(form.company), 300);
    return () => clearTimeout(timer);
  }, [form.company]);
  const companies = usePaginatedQuery<CrmCompany>(backendApi.crm.listCompaniesPage, { search: companySearch || undefined, namesOnly: true }, 50);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error('A name is required');
      return;
    }
    const validPhones = phones.filter((phone) => phone.number.trim());
    if (validPhones.length === 0) {
      toast.error('At least one phone number is required');
      return;
    }
    setSaving(true);
    try {
      const matches = await callBackend<Array<Array<{ id: string; name: string; kind: string; phones: string[] }>>>(backendApi.crm.previewContactMatches, { rows: [{ name: form.name, email: form.email || undefined, numbers: validPhones.map((phone) => phone.number) }] });
      const others = matches[0].filter((match) => match.id !== contact?.id);
      if (others.length && !confirm(`These numbers already match: ${others.map((match) => `${match.name} (${match.phones.join(', ')})`).join('; ')}. Save this contact anyway?`)) return;
      const result = (await saveContact({
        contactId: contact?.id,
        name: form.name,
        jobTitle: form.jobTitle || undefined,
        company: form.company || undefined,
        email: form.email || undefined,
        phones: validPhones,
        remark: form.remark || undefined,
        tags: form.tags,
        lifecycle: form.lifecycle || undefined,
        ownerExtension: form.ownerExtension || undefined,
        ownerName: form.ownerName || undefined,
        vip: form.vip,
        customFields: form.customFields,
      })) as { contactId: string; absorbed: number; syncing: boolean };
      toast.success(
        `${contact ? 'Contact updated' : 'Contact added'}${result.absorbed ? `, with earlier calls from ${result.absorbed === 1 ? 'that number' : 'those numbers'}` : ''}${
          result.syncing ? '. Updating the PBX phone book.' : ''
        }`
      );
      onSaved?.(result.contactId);
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={contact?.kind === 'contact' ? 'Edit contact' : 'New contact'} size="lg">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Name *" htmlFor="contact-name">
            <input id="contact-name" autoFocus value={form.name} onChange={(event) => set('name', event.target.value)} className={inputClass} placeholder="Full name" />
          </Field>
          <Field label="Job title" htmlFor="contact-title">
            <input id="contact-title" value={form.jobTitle} onChange={(event) => set('jobTitle', event.target.value)} className={inputClass} />
          </Field>
          <Field label="Company" htmlFor="contact-company">
            <input
              id="contact-company"
              list="crm-company-names"
              value={form.company}
              onChange={(event) => set('company', event.target.value)}
              className={inputClass}
              placeholder="Pick or type a new one"
            />
            <datalist id="crm-company-names">
              {companies.items.map((company) => (
                <option key={company.id} value={company.name} />
              ))}
            </datalist>
            {!companies.isLoading && !companies.isDone && <button type="button" onClick={companies.loadMore} disabled={companies.loadingMore} className="text-xs text-primary-600 hover:underline">Find more companies</button>}
          </Field>
          <Field label="Email" htmlFor="contact-email">
            <input id="contact-email" type="email" value={form.email} onChange={(event) => set('email', event.target.value)} className={inputClass} />
          </Field>
        </div>

        <Field label="Phone numbers *">
          <div className="space-y-2">
            {phones.map((phone, index) => (
              <div key={index} className="flex gap-2">
                <select
                  value={phone.type}
                  onChange={(event) => setPhones((current) => current.map((item, i) => (i === index ? { ...item, type: event.target.value } : item)))}
                  className={`${inputClass} w-36`}
                  aria-label="Number type"
                >
                  {PHONE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {getPhoneTypeLabel(type)}
                    </option>
                  ))}
                </select>
                <input
                  value={phone.number}
                  onChange={(event) => setPhones((current) => current.map((item, i) => (i === index ? { ...item, number: event.target.value } : item)))}
                  className={inputClass}
                  placeholder="0821234567"
                  inputMode="tel"
                />
                <button
                  type="button"
                  disabled={phones.length === 1}
                  onClick={() => setPhones((current) => current.filter((_, i) => i !== index))}
                  className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30"
                  aria-label="Remove number"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {phones.length < 9 && (
              <button
                type="button"
                onClick={() => setPhones((current) => [...current, { type: PHONE_TYPES.find((type) => !current.some((item) => item.type === type)) ?? 'other', number: '' }])}
                className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                <Plus className="w-4 h-4" /> Add number
              </button>
            )}
          </div>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Stage" htmlFor="contact-stage">
            <select id="contact-stage" value={form.lifecycle} onChange={(event) => set('lifecycle', event.target.value as Lifecycle | '')} className={inputClass}>
              <option value="">Not set</option>
              {Object.entries(LIFECYCLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Account owner" htmlFor="contact-owner" hint="Callbacks for this contact go to this extension.">
            <ExtensionPicker
              id="contact-owner"
              value={form.ownerExtension}
              onChange={(extension, name) => setForm((current) => ({ ...current, ownerExtension: extension, ownerName: name ?? '' }))}
            />
          </Field>
        </div>

        <Field label="Tags">
          <TagInput value={form.tags} onChange={(value) => set('tags', value)} suggestions={(tags ?? []).map((row) => row.tag)} />
        </Field>

        <Toggle checked={form.vip} onChange={(value) => set('vip', value)} label="VIP" description={`Missed calls from VIPs get a ${data?.settings.vipSlaMinutes ?? 10}-minute callback deadline and high priority.`} />

        {customFields.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {customFields.map((field) => (
              <Field key={field.key} label={field.label} htmlFor={`custom-${field.key}`}>
                {field.type === 'select' ? (
                  <select
                    id={`custom-${field.key}`}
                    value={form.customFields[field.key] ?? ''}
                    onChange={(event) => set('customFields', { ...form.customFields, [field.key]: event.target.value })}
                    className={inputClass}
                  >
                    <option value="">—</option>
                    {(field.options ?? []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`custom-${field.key}`}
                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    value={form.customFields[field.key] ?? ''}
                    onChange={(event) => set('customFields', { ...form.customFields, [field.key]: event.target.value })}
                    className={inputClass}
                  />
                )}
              </Field>
            ))}
          </div>
        )}

        <Field label="Phone book remark" htmlFor="contact-remark" hint="Stored on the PBX contact too. Use notes on the contact page for call notes.">
          <textarea id="contact-remark" value={form.remark} onChange={(event) => set('remark', event.target.value)} rows={2} className={inputClass} />
        </Field>

        {data?.features.phonebookSync && (
          <p className="text-xs text-gray-500 dark:text-gray-400">Saving also updates this contact in the PBX phone book, so desk phones show the name.</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={buttonClass.secondary}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className={buttonClass.primary}>
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
